/**
 * Karte unter einer KIAN-Antwort: der Entwurf, den KIAN vorbereitet hat (Tickets anlegen oder
 * ändern, Import aus einer Datei). Nichts davon ist geschrieben, bevor hier bestätigt wird.
 *
 * Aufbau: Kopf (was, Zustand) → Herkunft (Datei, Zuordnung, KIAN-Verbrauch) → Schalter →
 * Wertzuordnung → Einträge → Knöpfe. Während der Ausführung zeigt die Karte den Fortschritt je
 * Ticket; danach die angelegten Ticketnummern als Links ins Cockpit.
 */
import { useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  Collapse,
  Group,
  Loader,
  Modal,
  Paper,
  Progress,
  ScrollArea,
  SegmentedControl,
  Stack,
  Switch,
  Text,
  ThemeIcon,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconChevronDown,
  IconChevronUp,
  IconEdit,
  IconEye,
  IconListCheck,
  IconFileImport,
  IconPlayerPlay,
  IconRefresh,
  IconShieldCheck,
  IconTicket,
  IconTrash,
} from '@tabler/icons-react';
import { ApiError } from '../../../lib/api/http';
import type { KianActionItem, KianDraftIssue, KianTicketDraft, KianValueMapField } from '../../../lib/api/domains/kianActions';
import { KianUsageLine, kianUsageFrom } from '../../KianUsageLine';
import { KianActionItemRow } from './KianActionItemRow';
import { KianBulkEditModal } from './KianBulkEditModal';
import { KianDraftEditorModal } from './KianDraftEditorModal';
import { KianKnowledgeArticleCard } from './KianKnowledgeArticleCard';
import { KianTicketChangePreview } from './KianTicketChangePreview';
import { KianValueMapSection } from './KianValueMapSection';
import { useKianAction } from './useKianAction';
import {
  ACTION_STATUS_COLOR,
  ACTION_STATUS_LABEL,
  actionSubtitle,
  applyBulkChanges,
  isResumeOnly,
  resumeBulkChanges,
  executeLabel,
  isActionEditable,
  isItemEditable,
  isItemFinished,
  plural,
  type KianBulkChanges,
} from './kianActionUi';

const VISIBLE_ITEMS = 5;
const VALUE_MAP_FIELDS = new Set<string>(['customer', 'status', 'priority', 'category', 'assignee']);
const ID_FIELD: Record<string, keyof KianTicketDraft> = {
  customer: 'customerId',
  contact: 'contactId',
  status: 'statusId',
  priority: 'priorityId',
  category: 'categoryId',
  assignee: 'assigneeId',
};

type ItemFilter = 'all' | 'problems' | 'open' | 'done';

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.summary + (err.detail ? ` — ${err.detail}` : '');
  return err instanceof Error ? err.message : 'Unbekannter Fehler';
}

export function KianActionCard({
  actionId,
  compact = false,
  readOnly = false,
}: {
  actionId: string;
  compact?: boolean;
  /** Archiviertes Gespräch: nur ansehen. Der Server lehnt Schreiben dort ohnehin ab (409 SESSION_NOT_ACTIVE). */
  readOnly?: boolean;
}) {
  const { action, isLoading, error, update, execute, discard } = useKianAction(actionId);
  const [editing, setEditing] = useState<KianActionItem | null>(null);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [mapOpen, setMapOpen] = useState<boolean | null>(null);
  const [confirmUnknown, setConfirmUnknown] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [filter, setFilter] = useState<ItemFilter>('all');

  const busy = update.isPending || execute.isPending || discard.isPending;

  const derived = useMemo(() => {
    if (!action) return null;
    const included = action.items.filter((i) => i.include);
    const runnable = included.filter((i) => (i.status === 'PENDING' || i.status === 'FAILED') && i.ready);
    const unknown = included.filter((i) => i.status === 'UNKNOWN');
    const missingMap = action.valueMap.filter((e) => !e.targetId && e.origin !== 'manual').length;
    // Stapelarbeit: was sich noch ändern lässt, und davon das Ausgewählte
    const changeable = action.items.filter((i) => isItemEditable(i, action.kind));
    const selected = changeable.filter((i) => i.include);
    return { runnable, unknown, missingMap, changeable, selected };
  }, [action]);

  if (isLoading) {
    return (
      <Paper withBorder radius="md" p="sm" mt="xs">
        <Group gap="xs">
          <Loader size="xs" />
          <Text size="sm" c="dimmed">
            Entwurf wird geladen …
          </Text>
        </Group>
      </Paper>
    );
  }
  if (error || !action || !derived) {
    return (
      <Alert color="red" variant="light" mt="xs">
        Entwurf konnte nicht geladen werden: {errorMessage(error)}
      </Alert>
    );
  }
  // Anleitung für die Wissensdatenbank — eigene Karte (Text lesen, verbessern, einreichen)
  if (action.kind === 'KNOWLEDGE_ARTICLE') {
    return (
      <KianKnowledgeArticleCard action={action} controls={{ update, execute, discard }} compact={compact} readOnly={readOnly} />
    );
  }

  const editable = !readOnly && isActionEditable(action);
  const running = action.status === 'RUNNING';
  const create = action.kind === 'TICKET_CREATE';
  const isImport = action.source === 'FILE_IMPORT';
  const imp = action.notes.import;
  const usage = kianUsageFrom(action.notes.llmUsage);
  // Bei einem einzelnen Ticket steht die Auswahl direkt in der Zeile — die Zuordnung wäre doppelt.
  const showMap = action.valueMap.length > 0 && action.items.length > 1;
  const mapExpanded = mapOpen ?? derived.missingMap > 0;

  const filtered = action.items.filter((i) => {
    switch (filter) {
      case 'problems':
        return i.status === 'FAILED' || i.status === 'UNKNOWN' || (i.include && !i.ready && i.status === 'PENDING');
      case 'open':
        return !isItemFinished(i) && i.status !== 'SKIPPED';
      case 'done':
        return isItemFinished(i);
      default:
        return true;
    }
  });
  const many = action.items.length > VISIBLE_ITEMS + 1;
  const visible = many && !expanded ? filtered.slice(0, VISIBLE_ITEMS) : filtered;

  const run = (body?: { itemIds?: string[]; confirmUnknown?: boolean }, onSuccess?: () => void) =>
    execute.mutate(body, {
      onSuccess: () => onSuccess?.(),
      onError: (err) => {
        if (err instanceof ApiError && err.code === 'UNKNOWN_OUTCOME') {
          setConfirmUnknown(true);
          return;
        }
        notifications.show({ color: 'red', title: 'Nicht ausgeführt', message: errorMessage(err) });
      },
    });

  const patch = (p: Parameters<typeof update.mutate>[0], after?: () => void) =>
    update.mutate(p, {
      onSuccess: () => after?.(),
      onError: (err) => notifications.show({ color: 'red', title: 'Änderung nicht übernommen', message: errorMessage(err) }),
    });

  const setIncludeAll = (include: boolean) => {
    const items = derived.changeable.filter((i) => i.include !== include).map((i) => ({ id: i.id, include }));
    if (items.length) patch({ items });
  };

  const applyBulk = (changes: KianBulkChanges) =>
    patch(
      {
        items: derived.selected.map((i) => ({
          id: i.id,
          draft: applyBulkChanges(i.draft ?? {}, isResumeOnly(i, action.kind) ? resumeBulkChanges(changes) : changes),
        })),
      },
      () => {
        setBulkOpen(false);
        notifications.show({
          color: 'green',
          message: `${plural(derived.selected.length, 'Eintrag', 'Einträge')} geändert — noch nichts in weclapp geschrieben.`,
        });
      },
    );

  const onResolveIssue = (item: KianActionItem, issue: KianDraftIssue, candidateId: string) => {
    if (issue.mapKey && VALUE_MAP_FIELDS.has(issue.field)) {
      patch({ valueMaps: [{ field: issue.field as KianValueMapField, key: issue.mapKey, targetId: candidateId }] });
      return;
    }
    const idField = ID_FIELD[issue.field];
    if (!idField) return;
    patch({ items: [{ id: item.id, draft: { ...(item.draft ?? {}), [idField]: candidateId } }] });
  };

  // Vorschau zeigt, was noch passiert — Erledigtes steht im Ticket selbst (Nummer als Link)
  const previewable = (item: KianActionItem) => !isItemFinished(item) && item.status !== 'SKIPPED';
  const previewItem = previewId ? (action.items.find((i) => i.id === previewId) ?? null) : null;
  const previewRunnable =
    !!previewItem &&
    editable &&
    previewItem.include &&
    previewItem.ready &&
    (previewItem.status === 'PENDING' || previewItem.status === 'FAILED');
  const singlePreview = action.items.length === 1 && previewable(action.items[0]) ? action.items[0] : null;

  const icon = isImport ? <IconFileImport size={16} /> : create ? <IconTicket size={16} /> : <IconEdit size={16} />;
  const retrying = action.status === 'PARTIAL' || action.status === 'FAILED' || action.status === 'DONE';
  const runnableCount = derived.runnable.length;
  const blockedCount = action.counts.blocked;
  // Geparkt (weclapp war weg) zählt als erledigt — den Rest trägt der Nachsync nach
  const finished = action.counts.done + action.counts.queued;
  const progress = action.counts.included ? Math.round((finished / action.counts.included) * 100) : 0;

  return (
    <Paper withBorder radius="md" p="sm" mt="xs" data-kian-action={action.id}>
      <Stack gap="xs">
        {/* Am Handy rutscht das Badge unter den Titel, statt ihn Wort für Wort umbrechen zu lassen */}
        <Group justify="space-between" wrap="wrap" align="flex-start" gap="xs">
          <Group gap="xs" wrap="nowrap" style={{ minWidth: 0, flex: '1 1 12rem' }}>
            <ThemeIcon variant="light" color={ACTION_STATUS_COLOR[action.status]} size="md" radius="md">
              {icon}
            </ThemeIcon>
            <Stack gap={0} style={{ minWidth: 0 }}>
              <Text size="sm" fw={600} lineClamp={2}>
                {action.title}
              </Text>
              <Text size="xs" c="dimmed">
                {actionSubtitle(action)}
              </Text>
            </Stack>
          </Group>
          <Badge variant="light" color={ACTION_STATUS_COLOR[action.status]} style={{ flexShrink: 0 }}>
            {ACTION_STATUS_LABEL[action.status]}
          </Badge>
        </Group>

        {imp ? (
          <Stack gap={2}>
            <Text size="xs" c="dimmed">
              {imp.filename} · {imp.format.toUpperCase()} · {plural(imp.records, 'Datensatz', 'Datensätze')}
              {imp.skipped ? ` · ${imp.skipped} ausgelassen` : ''} ·{' '}
              {imp.mapping === 'deterministic'
                ? 'Spalten automatisch zugeordnet'
                : imp.mapping === 'llm'
                  ? 'Spalten von KIAN zugeordnet'
                  : 'von KIAN aus dem Text gelesen'}
            </Text>
            {imp.fieldMap?.length && !compact ? (
              <Text size="xs" c="dimmed" lineClamp={2}>
                {imp.fieldMap.map((f) => `${f.field} ← ${f.source}`).join(' · ')}
              </Text>
            ) : null}
            {imp.messages?.length ? (
              <Stack gap={0}>
                {imp.messages.slice(0, 4).map((m, i) => (
                  <Text key={i} size="xs" c="yellow">
                    {m}
                  </Text>
                ))}
              </Stack>
            ) : null}
          </Stack>
        ) : null}
        {usage ? <KianUsageLine usage={usage} /> : null}

        {editable && create && finished === 0 ? (
          <Group gap="md">
            <Tooltip
              multiline
              w={300}
              label="weclapp verschickt beim Anlegen seine E-Mail-Vorlagen (z. B. Eingangsbestätigung). Beim Import alter oder erledigter Vorgänge besser aus — danach verhalten sich offene Tickets wieder normal."
            >
              <Switch
                size="xs"
                label="Kunden per weclapp-Mail benachrichtigen"
                checked={action.options.notifyCustomer}
                disabled={busy}
                onChange={(e) => {
                  const on = e.currentTarget.checked;
                  patch({ options: { notifyCustomer: on } });
                }}
              />
            </Tooltip>
            <Switch
              size="xs"
              label="Kommentare ohne Angabe intern"
              checked={action.options.commentsInternal}
              disabled={busy}
              onChange={(e) => {
                const on = e.currentTarget.checked;
                patch({ options: { commentsInternal: on } });
              }}
            />
          </Group>
        ) : null}

        {showMap ? (
          <Box>
            <Button
              variant="subtle"
              size="compact-xs"
              color={derived.missingMap ? 'red' : 'gray'}
              rightSection={mapExpanded ? <IconChevronUp size={12} /> : <IconChevronDown size={12} />}
              onClick={() => setMapOpen(!mapExpanded)}
            >
              Zuordnung{derived.missingMap ? ` — ${plural(derived.missingMap, 'Wert offen', 'Werte offen')}` : ` (${action.valueMap.length})`}
            </Button>
            <Collapse expanded={mapExpanded}>
              <Box mt={6}>
                <KianValueMapSection
                  entries={action.valueMap}
                  editable={editable}
                  busy={busy}
                  onChange={(field, key, targetId) => patch({ valueMaps: [{ field, key, targetId }] })}
                />
              </Box>
            </Collapse>
          </Box>
        ) : null}

        {running || (finished > 0 && action.items.length > 1) ? (
          <Stack gap={2}>
            <Progress value={progress} size="sm" animated={running} color={action.status === 'DONE' ? 'green' : 'blue'} />
            <Text size="xs" c="dimmed">
              {finished.toLocaleString('de-DE')} von {action.counts.included.toLocaleString('de-DE')} erledigt
              {action.counts.queued ? ` · ${action.counts.queued} in der weclapp-Warteliste` : ''}
              {action.counts.failed ? ` · ${action.counts.failed} fehlgeschlagen` : ''}
              {action.counts.unknown ? ` · ${action.counts.unknown} unklar` : ''}
            </Text>
          </Stack>
        ) : null}

        {action.items.length > VISIBLE_ITEMS && !compact ? (
          <SegmentedControl
            size="xs"
            value={filter}
            onChange={(v) => setFilter(v as ItemFilter)}
            data={[
              { value: 'all', label: `Alle (${action.items.length})` },
              { value: 'problems', label: 'Offene Fragen' },
              { value: 'open', label: 'Nicht erledigt' },
              { value: 'done', label: 'Erledigt' },
            ]}
          />
        ) : null}

        {editable && !running && !compact && action.items.length > 1 && derived.changeable.length > 0 ? (
          <Group justify="space-between" gap="xs" wrap="wrap">
            <Text size="xs" c="dimmed">
              {derived.selected.length.toLocaleString('de-DE')} von {derived.changeable.length.toLocaleString('de-DE')} ausgewählt
            </Text>
            <Group gap={4} wrap="nowrap">
              <Button
                size="compact-xs"
                variant="subtle"
                color="gray"
                disabled={busy || derived.selected.length === derived.changeable.length}
                onClick={() => setIncludeAll(true)}
              >
                Alle
              </Button>
              <Button
                size="compact-xs"
                variant="subtle"
                color="gray"
                disabled={busy || derived.selected.length === 0}
                onClick={() => setIncludeAll(false)}
              >
                Keine
              </Button>
              <Tooltip label="Kunde, Status, Priorität, Kategorie, Bearbeiter oder Frist für alle ausgewählten auf einmal setzen">
                <Button
                  size="compact-xs"
                  variant="light"
                  leftSection={<IconListCheck size={12} />}
                  disabled={busy || derived.selected.length === 0}
                  onClick={() => setBulkOpen(true)}
                >
                  Auswahl bearbeiten
                </Button>
              </Tooltip>
            </Group>
          </Group>
        ) : null}

        <ScrollArea.Autosize mah={expanded ? 480 : undefined} type="auto" offsetScrollbars>
          <Stack gap={0}>
            {visible.map((item) => (
              <KianActionItemRow
                key={item.id}
                action={action}
                item={item}
                busy={busy}
                compact={compact}
                onToggleInclude={
                  !readOnly && action.items.length > 1 ? (it, include) => patch({ items: [{ id: it.id, include }] }) : undefined
                }
                onEdit={readOnly ? undefined : setEditing}
                onPreview={previewable(item) ? (it) => setPreviewId(it.id) : undefined}
                onResolveIssue={readOnly ? undefined : onResolveIssue}
              />
            ))}
            {!visible.length ? (
              <Text size="xs" c="dimmed" py="xs">
                Keine Einträge in dieser Ansicht.
              </Text>
            ) : null}
          </Stack>
        </ScrollArea.Autosize>
        {many ? (
          <Button variant="subtle" size="compact-xs" onClick={() => setExpanded((v) => !v)}>
            {expanded ? 'Weniger anzeigen' : `Alle ${filtered.length.toLocaleString('de-DE')} anzeigen`}
          </Button>
        ) : null}

        {readOnly && !running && action.status !== 'DISCARDED' && (derived.runnable.length > 0 || derived.unknown.length > 0) ? (
          <Text size="xs" c="dimmed">
            Das Gespräch ist archiviert. Zum Ausführen oder Ändern erst wieder aktivieren.
          </Text>
        ) : null}
        {!readOnly && !running && action.status !== 'DISCARDED' ? (
          <Group justify="space-between" mt={4} wrap="wrap" gap="xs">
            <Group gap="xs">
              {runnableCount > 0 && editable ? (
                <Tooltip
                  disabled={!blockedCount}
                  label={`${plural(blockedCount, 'Eintrag bleibt', 'Einträge bleiben')} liegen, bis die offene Frage beantwortet ist.`}
                >
                  <Button
                    size="xs"
                    color="cyberOrange"
                    leftSection={retrying ? <IconRefresh size={14} /> : <IconPlayerPlay size={14} />}
                    loading={execute.isPending}
                    disabled={busy}
                    onClick={() => run()}
                  >
                    {retrying && finished + action.counts.failed > 0
                      ? `${runnableCount === 1 ? 'Offenen Eintrag' : `${runnableCount.toLocaleString('de-DE')} offene Einträge`} ausführen`
                      : executeLabel(action, runnableCount)}
                  </Button>
                </Tooltip>
              ) : null}
              {singlePreview && editable ? (
                <Button
                  size="xs"
                  variant="light"
                  leftSection={<IconEye size={14} />}
                  onClick={() => setPreviewId(singlePreview.id)}
                >
                  Vorschau
                </Button>
              ) : null}
              {derived.unknown.length ? (
                <Button
                  size="xs"
                  variant="light"
                  color="orange"
                  leftSection={<IconShieldCheck size={14} />}
                  disabled={busy}
                  onClick={() => setConfirmUnknown(true)}
                >
                  Prüfen & fortsetzen ({derived.unknown.length})
                </Button>
              ) : null}
              {runnableCount === 0 && blockedCount > 0 && editable ? (
                <Text size="xs" c="red">
                  {plural(blockedCount, 'Eintrag braucht', 'Einträge brauchen')} noch eine Entscheidung.
                </Text>
              ) : null}
            </Group>
            {editable && finished === 0 ? (
              <Button
                size="xs"
                variant="subtle"
                color="gray"
                leftSection={<IconTrash size={14} />}
                disabled={busy}
                onClick={() => setConfirmDiscard(true)}
              >
                Verwerfen
              </Button>
            ) : null}
          </Group>
        ) : null}
        {!readOnly && !running && action.status === 'PROPOSED' ? (
          <Text size="xs" c="dimmed">
            {create
              ? isImport
                ? 'Noch nichts angelegt. Tickets entstehen erst mit dem Klick — in der Reihenfolge der Liste.'
                : 'Noch nichts angelegt. Das Ticket entsteht erst mit dem Klick.'
              : 'Noch nichts geändert. Die Änderung wird erst mit dem Klick geschrieben.'}
          </Text>
        ) : null}
      </Stack>

      <KianDraftEditorModal
        opened={editing !== null}
        onClose={() => setEditing(null)}
        kind={action.kind}
        item={editing}
        saving={update.isPending}
        onSave={(draft) => editing && patch({ items: [{ id: editing.id, draft }] }, () => setEditing(null))}
      />

      <KianTicketChangePreview
        opened={previewItem !== null}
        onClose={() => setPreviewId(null)}
        action={action}
        item={previewItem}
        onEdit={
          readOnly
            ? undefined
            : (it) => {
                setPreviewId(null);
                setEditing(it);
              }
        }
        onExecute={
          previewRunnable && !readOnly && !running
            ? (it) => run({ itemIds: [it.id] }, () => setPreviewId(null))
            : undefined
        }
        executing={execute.isPending}
      />

      <KianBulkEditModal
        opened={bulkOpen}
        onClose={() => setBulkOpen(false)}
        kind={action.kind}
        count={derived.selected.length}
        saving={update.isPending}
        onApply={applyBulk}
      />

      <Modal
        opened={confirmUnknown}
        onClose={() => setConfirmUnknown(false)}
        title="Unterbrochene Einträge fortsetzen"
        centered
        size="md"
        zIndex={5300}
      >
        <Stack gap="sm">
          <Text size="sm">
            {plural(derived.unknown.length, 'Eintrag wurde', 'Einträge wurden')} mitten im Schreiben unterbrochen (z. B. durch
            einen Neustart). Vor dem Fortsetzen prüft das Portal in weclapp, was schon angekommen ist: Ein bereits angelegtes
            Ticket wird übernommen statt doppelt angelegt, ein schon geschriebener Kommentar nicht noch einmal gesendet.
          </Text>
          <Text size="sm" c="dimmed">
            Lässt sich das nicht eindeutig klären, bleibt der Eintrag stehen — dann bitte im Ticket nachsehen.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setConfirmUnknown(false)}>
              Abbrechen
            </Button>
            <Button
              color="orange"
              loading={execute.isPending}
              onClick={() => {
                setConfirmUnknown(false);
                run({ itemIds: [...derived.unknown.map((i) => i.id), ...derived.runnable.map((i) => i.id)], confirmUnknown: true });
              }}
            >
              Prüfen & fortsetzen
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal opened={confirmDiscard} onClose={() => setConfirmDiscard(false)} title="Entwurf verwerfen?" centered size="sm" zIndex={5300}>
        <Stack gap="sm">
          <Text size="sm">Der Entwurf wird verworfen. In weclapp wurde nichts geschrieben.</Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setConfirmDiscard(false)}>
              Behalten
            </Button>
            <Button
              color="red"
              loading={discard.isPending}
              onClick={() =>
                discard.mutate(undefined, {
                  onSuccess: () => setConfirmDiscard(false),
                  onError: (err) => notifications.show({ color: 'red', title: 'Nicht verworfen', message: errorMessage(err) }),
                })
              }
            >
              Verwerfen
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Paper>
  );
}

/**
 * Alle Karten einer Antwort. `superseded`: Entwürfe, die KIAN in einer späteren Antwort
 * überarbeitet hat (Anleitungen) — hier nur ein Verweis, die Karte steht unter der neuesten.
 * Zwei gleiche Karten im Verlauf ließen offen, welche gilt.
 */
export function KianActionCards({
  actionIds,
  compact,
  readOnly,
  superseded,
}: {
  actionIds: string[];
  compact?: boolean;
  readOnly?: boolean;
  superseded?: ReadonlySet<string>;
}) {
  if (!actionIds.length) return null;
  return (
    <Stack gap={0}>
      {actionIds.map((id) =>
        superseded?.has(id) ? (
          <Text key={id} size="xs" c="dimmed" mt={6}>
            Entwurf überarbeitet — die aktuelle Fassung steht weiter unten.
          </Text>
        ) : (
          <KianActionCard key={id} actionId={id} compact={compact} readOnly={readOnly} />
        ),
      )}
    </Stack>
  );
}
